<script>
import { mapGetters } from 'vuex'
import TheTopBar from '@/components/TheTopBar'
import MainNavMenu from '@/components/MainNavMenu/MainNavMenu'
import MainNavAvatar from '@/components/MainNavAvatar/MainNavAvatar'
import TheFooter from '@/components/TheFooter'
import SuperAdminTools from '@/components/admin/superAdminTools/SuperAdminTools'
import axios from 'axios'
import SupportSection from '@/components/MainNavAvatar/SupportSection.vue'

export default {
  name: 'App',
  components: {
    TheTopBar,
    MainNavMenu,
    MainNavAvatar,
    SuperAdminTools,
    TheFooter,
    // PromoLinksOverlay,
    SupportSection
  },
  data () {
    return {
      showNavPanel: true,
      scrollPosition: 0,
      refreshKey: 0
    }
  },
  computed: {
    ...mapGetters({
      sidenavState: 'application/sidenavState',
      clipboardState: 'application/clipboardState',
      userNotifications: 'notifications/userNotifications',
      links: 'helpLinks/links',
      user: 'user/isPatient',
      globalLoading: 'application/globalLoading'
    }),
    showHelpButtons () {
      return (this.user.isPatient === false)
    },
    userInfo () {
      return this.$store.state.user.userInfo
    },
    emailHelp () {
      return this.$store.state.user.supportEmail
    },
    currentYear () {
      return this.$dateFns.getYear(new Date())
    },
    showSupport () {
      return this.$userHasPermission('p:app-tenant-scope')
    },
    isDemoMode () {
      return this.$store.state.user.scopeAppTenant !== null
    },
    demoTenantName () {
      const t = this.$store.state.user.scopeAppTenant
      return t ? t.name : ''
    },
    recentException () {
      return this.$store.state.app.recentException
    },
    showClipboard () {
      return (this.clipboardState === 'ACTIVE')
    },
    isMobile () {
      return ((this.$mq === 'mobile') || (this.$mq === 'tablet'))
    },
    currentRoute () {
      return this.$router.currentRoute
    },
    sidebarMobile () {
      // if nav is already open, leave it open, else hide
      let sbMobile = 'hide'
      if (this.sidebarPosition === 'fixed') {
        if (this.sidenavState === 'ACTIVE') {
          sbMobile = 'show'
        }
      }
      return sbMobile
    },
    sidebarPosition () {
      let positioning
      switch (this.$mq) {
        case 'mobile':
          positioning = 'fixed'
          break
        case 'tablet':
          positioning = 'fixed'
          break
        case 'desktop':
          positioning = 'static'
          break
        case 'widescreen':
          positioning = 'static'
          break
        case 'fullhd':
          positioning = 'static'
          break
        default:
      }
      return positioning
    },
    expandNavOnHover () {
      return true
    },
    reduceNav () {
      return false
    },
    showPlayOverlay () {
      return location.search.includes('state=play')
    },
    // showNotifications () {
    //   const isSuperAdmin = this.userInfo.permissions ? this.userInfo.permissions.includes('p:super-admin') : false
    //   return (this.isLoggedIn && !isSuperAdmin)
    // },
    isLoggedIn () {
      return this.$store.getters['user/isLoggedIn']
    },
    isAuthPage () {
      return ['Login', 'ForgotPassword', 'ResetPassword', 'PreLogout', 'SessionExpired', 'SignUp'].includes(this.$route.name)
    },
    // Clinical test screens run distraction-free: no sidebar, no marketing
    // footer. Concussion patients shouldn't be navigating the app mid-test.
    isFocusPage () {
      return this.$route.meta && this.$route.meta.focusMode === true
    },
    showSidebar () {
      return this.isLoggedIn && !this.isAuthPage && !this.isFocusPage
    },
    domain () {
      return window.location.origin
    }
  },
  watch: {
    recentException () {
      if (this.recentException) {
        if (this.recentException === 'LOGOUT') {
          this.$store.dispatch('clearRecentException')
          this.forceLogout('Your session has expired.  Please login again to continue.')
        } else if (this.recentException === 'USER_EXISTS') {
          this.$buefy.dialog.alert({
            message: 'A user with this email already exists',
            trapFocus: true
          })
          this.$store.dispatch('clearRecentException')
        } else if (this.recentException === 'PREVIOUS_FREE_TRIAL') {
          this.$buefy.dialog.alert({
            message: 'Your Company or email address has been previously used for a Free Trial or OVB account.',
            trapFocus: true
          })
          this.$store.dispatch('clearRecentException')
        } else {
          this.$buefy.dialog.alert({
            message: `A Server Error Has Occurred ${this.$userHasPermission('p:super-admin') ? `\n\n${this.$store.state.app.recentException}` : ''}`,
            trapFocus: true
          })
          this.$store.dispatch('clearRecentException')
        }
      }
    }
  },
  methods: {
    onOpenSideNav () {
      this.showNavPanel = !this.showNavPanel
    },
    toggleSideNav () {
      let payload
      switch (this.sidenavState) {
        case 'ACTIVE':
          payload = 'HIDDEN'
          break
        case 'HIDDEN':
          payload = 'ACTIVE'
          break
        default:
      }
      this.$store.dispatch('application/setSidenavState', payload)
    },
    closeClipboard () {
      this.$store.dispatch('application/setClipboardState', 'HIDDEN')
    },
    exitDemo () {
      this.$store.dispatch('CLEAR_SCOPE_APP_TENANT')
    },
    async forceLogout (message) {
      try {
        await axios.get('/logout', { withCredentials: true })
      } catch (e) {
        // Proceed with client-side cleanup even if server logout fails
      }
      this.$store.dispatch('clearRecentException')
      this.$store.dispatch('brochure/setGuestUser', null)
      this.$store.dispatch('user/logout')
      this.$store.dispatch('app/logout')
      this.$store.dispatch('RESET_OVB')
        .then(() => {
          window.location.href = '/login?logout=expired'
        })
    }
  },
  created () {
    // Always clear globalLoading on app startup — prevents stuck black overlay
    // from persisted Vuex state (e.g. after deploy causes stale JS chunk error)
    this.$store.dispatch('application/setGlobalLoading', { show: false, message: '' })

    // Safety net: if globalLoading ever gets set to show, force-hide after 5 seconds
    // This guarantees users NEVER get a permanent black screen
    this.$store.watch(
      (state) => state.application.globalLoading.show,
      (show) => {
        if (show) {
          clearTimeout(this._globalLoadingTimeout)
          this._globalLoadingTimeout = setTimeout(() => {
            this.$store.dispatch('application/setGlobalLoading', { show: false, message: '' })
          }, 5000)
        }
      }
    )

    // Heartbeat: confirm user actually reached the app UI (fire-and-forget)
    this.$watch('showSidebar', (show) => {
      if (show && !this._heartbeatSent) {
        this._heartbeatSent = true
        axios.post('/api/auth/heartbeat', {}, { withCredentials: true }).catch(() => {})
      }
    }, { immediate: true })

    window.addEventListener('scroll', () => {
      this.scrollPosition = window.top.scrollY
    })
  },
  destroyed () {
    window.removeEventListener('scroll', () => {
      this.scrollPosition = window.top.scrollY
    })
  }
}
</script>

<template>
  <div
    id="app"
    :class="{'side-nav-active': sidenavState === 'ACTIVE'}"
  >
    <!-- Global loading overlay for login/logout/session expiry -->
    <div
      v-if="globalLoading.show"
      id="global-loading-overlay"
      class="is-active"
    >
      <div class="overlay-logo">
        <img
          src="/img/ovb_logo.svg"
          aria-hidden="true"
          alt="Our Visual Brain logo"
        />
        <div
          class="overlay-loading-spinner mt-5"
        >
          <div class="loader"></div>
        </div>
        <div
          class="has-text-white is-size-5 is-family-light mt-2"
        >
          {{ globalLoading.message }}
        </div>
      </div>
    </div>

    <div
      v-if="showPlayOverlay"
      id="play-overlay"
      :class="{ 'is-active': showPlayOverlay }"
    >
      <div class="overlay-logo">
        <img
          src="/img/ovb_logo.svg"
          aria-hidden="true"
          alt="Our Visual Brain logo"
        />
        <div
          class="overlay-loading-spinner mt-5"
        >
          <div class="loader"></div>
        </div>
        <div
          class="has-text-white is-size-5 is-family-light mt-2"
        >
          Preparing Your Activity...
        </div>
      </div>
    </div>
    <div
      v-show="!showPlayOverlay"
      class="site-layout"
      :class="{'side-nav-active': sidenavState === 'ACTIVE'}"
    >
      <aside
        v-if="showSidebar"
        :class="{'active': sidenavState === 'ACTIVE'}"
        class="sidebar-panel"
      >
        <div class="sidebar-content">
          <div
            class="sidebar-top"
            :class="{'mb-3': !showSupport}"
          >
            <main-nav-avatar />
            <button
              aria-expanded="false"
              class="sidenav-toggle button"
              @click="toggleSideNav"
              aria-label="Hide side navigation"
            >
                  <span class="icon is-large">
                    <i
                      class="mdi mdi-backburger mdi-36px"
                      aria-hidden="true"
                    />
                  </span>
            </button>
          </div>
          <support-section
            v-if="showSupport"
            class="mb-5"
          />
          <main-nav-menu />
        </div>
      </aside>
      <div
        :class="{'active': sidenavState === 'ACTIVE'}"
        class="sidebar-mask"
        @click="toggleSideNav"
      />
      <div
        class="view-screen"
      >
        <header
          v-if="showSidebar"
          class="app-header"
          :class="{'is-fixed': scrollPosition > 0, 'is-logged-in': showSidebar }"
        >
          <the-top-bar
            @OPEN_SIDE_NAVIGATION_PANEL="onOpenSideNav"
          />
        </header>
        <div
          class="content-wrapper"
        >
          <main
            class="content-area"
            :class="{'container': showSidebar, 'is-logged-in': showSidebar}"
          >
            <transition
              name="fade"
              mode="out-in"
            >
              <div
                :key="$route.fullPath"
                class="page-content"
                :class="{'page-margin': showSidebar}"
              >
                <div id="floating-actions" />
                <router-view
                  :class="{'is-logged-in': showSidebar }"
                />
              </div>
            </transition>
          </main>
        </div>
        <the-footer v-if="!isAuthPage && !isFocusPage" />
      </div>
      <aside
        v-if="showSidebar"
      >
        <b-sidebar
          id="main-right-sidebar"
          :fullheight="true"
          :right="true"
          position="fixed"
          v-model="showClipboard"
          :can-cancel="[]"
        >
          <template>
            <super-admin-tools />
          </template>
        </b-sidebar>
      </aside>
    </div>
    <svg
      height="0"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id="outline">
          <feMorphology in="SourceAlpha" result="DILATED" operator="dilate" radius="4"></feMorphology>

          <feFlood flood-color="#F3EED9" flood-opacity="0.15" result="PINK"></feFlood>
          <feComposite in="PINK" in2="DILATED" operator="in" result="OUTLINE"></feComposite>
          <feGaussianBlur in="OUTLINE" stdDeviation="2" result="FUZZYOUTLINE" />
          <feMerge>
            <feMergeNode in="FUZZYOUTLINE" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>
<!--    <div class="test-info-overlay">{{ $route.name }}</div>-->
  </div>
</template>

<style lang="scss">
  @import "assets/scss/_common";
  body {
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
  .test-info-overlay {
    position: absolute;
    padding: 1rem;
    background-color: red;
    color: #fff;
    top: 0;
    left: 0;
    z-index: 9999;
  }
  .notifications-container {}
  #app {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: 100vh;
    @include desktop {
      &.side-nav-active {
        footer.ovb-footer {
          padding: 2rem 1rem 1rem 1rem;
        }
      }
    }
  }
  .view-screen {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    // Flex items default to min-width:auto and refuse to shrink below their
    // content, so a table wider than the viewport pushed the whole page
    // sideways instead of scrolling inside its own area. The header bar and
    // footer then ended short of the right edge.
    min-width: 0;
  }
  .site-layout {
    display: flex;
    flex-grow: 1;
    min-width: 0;
    .app-header {
      display: flex;
      width: 100vw;
       height: $topBarHeight;
      -webkit-transition: all 0.45s;
      transition: all 0.45s ease-in-out;
      &.is-fixed {
        position: fixed;
        z-index: 60;
        box-shadow: $shadow-3dp;
        width: 100vw;
      }
    }
    &.side-nav-active {
      .app-header {
        @include desktop {
          width: calc(100vw - #{$navPanelWidth});
        }
      }
    }
  }
  .content-wrapper {
    flex-grow: 1;
    display: flex;
    min-width: 0;
  }
  main.content-area {
    display: flex;
    min-width: 0;
    &:not(.is-logged-in) {
      flex-grow: 1;
    }
  }
  .page-content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-width: 0;
    &.is-logged-in {
      margin: 1rem;
    }
  }
  .sidebar-mask {
    display: none;
    &.active {
      display: flex;
      background-color: transparent;
      flex: 1;
      width: 100%;
      position: fixed;
      top: 0;
      bottom: 0;
      z-index: 70;
      @include desktop {
        display: none;
      }
    }
  }
  .sidebar-panel {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    width: $navPanelWidth;
    max-width: $navPanelWidth;
    min-width: $navPanelWidth;
    background-color: $text;
    margin-left: -$navPanelWidth;
    -webkit-transition: all 0.45s;
    transition: all 0.45s ease-in-out;
    @include mobile {
      position: fixed;
      min-height: 100vh;
      z-index: 80;
      &.active {
        box-shadow: 0 0 12px 2px rgba(0, 0, 0, .5);
      }
    }
    @include tablet {
      position: fixed;
      min-height: 100vh;
      z-index: 80;
      &.active {
        box-shadow: 0 0 12px 2px rgba(0, 0, 0, .5);
      }
    }
    @include desktop {
      position: relative;
      z-index: 0;
      &.active {
        box-shadow: none;
      }
    }
    @include fullhd {
      position: relative;
      z-index: 0;
      &.active {
        box-shadow: none;
      }
    }
    @include widescreen {
      position: relative;
      z-index: 0;
      &.active {
        box-shadow: none;
      }
    }
    &.active {
      margin-left: 0;
    }
    .sidebar-top {
      display: flex;
    }
    .sidebar-content {
      height: 100vh;
      background-color: $text;
      width: $navPanelWidth;
      max-width: $navPanelWidth;
      min-width: $navPanelWidth;
      position: fixed;
      flex-grow: 1;
      display: flex;
      flex-direction: column;
    }
    button.button.sidenav-toggle {
      background-color: transparent;
      border: none;
      border-radius: 0;
      margin-top: 0.5rem;
      i {
        color: $primary;
      }
    }
    hr.nav-section-separator {
      height: 1px;
      opacity: .25;
      margin: 2rem 0 0 0;
    }
  }
  #main-right-sidebar.b-sidebar {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    .sidebar-content.is-fixed.is-right {
      top: $topBarHeight;
      box-shadow: 5px 10px 13px 3px rgba(10, 10, 10, 0.1);
    }
    .close-button {
      margin: 1rem;
    }
  }
  .copyright-notice {
    margin-top: 1rem;
    flex: 1;
    font-size: $size-6;
  }
  //footer.ovb-footer {
  //  background-color: $gray-darkest;
  //  padding: 2rem 0 1rem 0;
  //  display: flex;
  //  flex-direction: column;
  //  color: $gray-light;
  //}
  //.ovb-text-logo {
  //  width: 15rem;
  //  opacity: .5;
  //}
  //.ovb-footer-content {
  //  flex: 1;
  //  .footer-separator {
  //    border-top: 0.5px solid $gray-light;
  //    opacity: 0.25;
  //  }
  //  .content-row {
  //    padding: 0 1rem;
  //    display: flex;
  //    flex-direction: column;
  //    flex-wrap: wrap;
  //    justify-content: space-between;
  //    @include tablet {
  //      flex-direction: row;
  //    }
  //    @include desktop {
  //      padding: 0;
  //    }
  //  }
  //  .nav-list {
  //    display: flex;
  //    flex-direction: column;
  //    .nav-title {
  //      font-family: $family-semibold;
  //      font-size: 1.25rem;
  //      opacity: 0.5;
  //    }
  //    .footer-link {
  //      color: $gray-light;
  //      //font-family: $family-semibold;
  //      &:hover {
  //        text-decoration: underline;
  //      }
  //    }
  //  }
  //}

  #global-loading-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    background-color: #000;
    color: rgba(var(--text-color));
    overflow: hidden;
    cursor: wait;
    &.is-active {
      display: flex !important;
      justify-content: center;
      align-items: center;
    }
    .overlay-logo {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      max-width: 40rem;
      min-width: 20rem;
      img {
        display: block;
      }
    }
    .overlay-loading-spinner {
      position: relative;
      flex: 1;
      width: 3rem;
      height: 3rem;
      opacity: .3;
    }
  }

  #play-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 900;
    background-color: #000;
    color: rgba(var(--text-color));
    overflow: hidden;
    cursor: wait;
    transition: all 3s ease-in-out;
    &.is-active {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .overlay-logo {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      max-width: 40rem;
      min-width: 20rem;
      img {
        display: block;
      }
    }
    .overlay-loading-spinner {
      position: relative;
      flex: 1;
      width: 3rem;
      height: 3rem;
      opacity: .3;
    }
    .loader {
      width: 48px;
      height: 48px;
      border: 5px solid #FFF;
      border-bottom-color: transparent;
      border-radius: 50%;
      display: inline-block;
      box-sizing: border-box;
      animation: rotation 1s linear infinite;
    }

    @keyframes rotation {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }
</style>
<!-- deploy test 6 -->
